import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import "./AtivosPage.css";
import "../MaintenanceTheme.css";

import CriarModal from "./popups/CriarModal.jsx";
import EditarModal from "./popups/EditarModal.jsx";
import ExcluirModal from "./popups/ExcluirModal.jsx";
import AtivarModal from "./popups/AtivarModal.jsx";
import InativarModal from "./popups/InativarModal.jsx";

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");
const SAFE = (v) => (v == null || v === "" ? "-" : v);

const LABELS = {
  ativos: "ATIVOS",
  grupo: "GRUPO",
  responsavel: "RESPONSAVEL",
  telefone_do_responsavel: "TELEFONE",
  atividade: "ATIVIDADE",
  empresa: "EMPRESA",
  coordenador: "COORDENADOR",
  operacao: "OPERACAO",
};

const ORDER = [
  "ativos",
  "grupo",
  "responsavel",
  "telefone_do_responsavel",
  "atividade",
  "empresa",
  "coordenador",
  "operacao",
];

const COLW = {
  ativos: "17%",
  grupo: "8%",
  responsavel: "15%",
  telefone_do_responsavel: "10%",
  atividade: "12%",
  empresa: "10%",
  coordenador: "10%",
  operacao: "8%",
  controles: "10%",
};

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="knob" />
    </button>
  );
}

function computeStats(list) {
  const byGroup = {};
  let ativosOn = 0;

  for (const r of list) {
    if (Number(r?.ativo)) ativosOn++;
    const g = UP(r?.grupo || "-");
    byGroup[g] = (byGroup[g] || 0) + 1;
  }

  const inativos = list.length - ativosOn;
  const porGrupo = Object.entries(byGroup).sort((a, b) =>
    a[0].localeCompare(b[0], "pt-BR", { sensitivity: "base" })
  );

  return { total: list.length, ativos: ativosOn, inativos, porGrupo };
}

const BASE = "/ativos";

export default function AtivosPage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [grupoFiltro, setGrupoFiltro] = useState(null);

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [selecionada, setSelecionada] = useState(null);

  useEffect(() => {
    document.body.classList.add("ativ-dark");
    return () => document.body.classList.remove("ativ-dark");
  }, []);

  async function carregar({ silent = false } = {}) {
    if (!silent) setLoading(true);
    setErr("");

    try {
      const r = await api.get(`${BASE}/listar`);
      setRows(r.data?.data || []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Erro ao listar");
      setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  const stats = useMemo(() => computeStats(rows), [rows]);

  const filtradas = useMemo(() => {
    const arr = grupoFiltro ? rows.filter((r) => UP(r?.grupo) === UP(grupoFiltro)) : rows;
    const a = [...arr];
    a.sort((x, y) =>
      String(x?.ativos || "").localeCompare(String(y?.ativos || ""), "pt-BR", { sensitivity: "base" })
    );
    return a;
  }, [rows, grupoFiltro]);

  async function criarAtivo(payload) {
    try {
      const r = await api.post(`${BASE}/cadastrar`, payload);
      const id = r.data?.id;
      setRows((prev) => [{ id, ...payload, ativo: 1 }, ...prev]);
      setOpenCriar(false);
      carregar({ silent: true });
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao cadastrar");
    }
  }

  async function editarAtivo(payload) {
    if (!selecionada?.id) return;
    const id = selecionada.id;

    try {
      await api.put(`${BASE}/editar/${id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...payload } : r)));
      setOpenEditar(false);
      setSelecionada(null);
      carregar({ silent: true });
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao editar");
    }
  }

  async function excluirAtivo() {
    if (!selecionada?.id) return;
    const id = selecionada.id;

    try {
      await api.delete(`${BASE}/excluir/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setOpenExcluir(false);
      setSelecionada(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao excluir");
    }
  }

  async function setAtivo(ativo) {
    if (!selecionada?.id) return;
    const id = selecionada.id;

    try {
      await api.patch(`${BASE}/alterar-ativo/${id}`, { ativo });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ativo } : r)));
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao alterar status");
    } finally {
      setOpenAtivar(false);
      setOpenInativar(false);
      setSelecionada(null);
    }
  }

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  function renderColGroup() {
    return (
      <colgroup>
        <col style={{ width: COLW.ativos }} />
        <col style={{ width: COLW.grupo }} />
        <col style={{ width: COLW.responsavel }} />
        <col style={{ width: COLW.telefone_do_responsavel }} />
        <col style={{ width: COLW.atividade }} />
        <col style={{ width: COLW.empresa }} />
        <col style={{ width: COLW.coordenador }} />
        <col style={{ width: COLW.operacao }} />
        <col style={{ width: COLW.controles }} />
      </colgroup>
    );
  }

  return (
    <div className="app-shell maint-theme maint-ativos">
      <div className="topbar">
        <button className="btn-hero left" type="button" onClick={goHome}>
          Voltar para o início
        </button>
        <h1 className="title">Catálago de Ativos</h1>
        <button className="btn-hero right" type="button" onClick={() => setOpenCriar(true)}>
          Cadastrar
        </button>
      </div>

      <div className="cards">
        <div className="card">
          <div className="card__label">ATIVOS (TOTAL)</div>
          <div className="card__value">{stats.total}</div>
        </div>
        <div className="card card--ok">
          <div className="card__label">ATIVOS (ON)</div>
          <div className="card__value">{stats.ativos}</div>
        </div>
        <div className="card card--alert">
          <div className="card__label">INATIVOS</div>
          <div className="card__value">{stats.inativos}</div>
        </div>

        <div className="card card--groups">
          <div className="card__label">POR GRUPO</div>
          <div className="group-list">
            {stats.porGrupo.map(([g, qtd]) => (
              <button
                key={g}
                className={`chip ${grupoFiltro && UP(grupoFiltro) === UP(g) ? "active" : ""}`}
                type="button"
                onClick={() => setGrupoFiltro((prev) => (prev && UP(prev) === UP(g) ? null : g))}
                title={`Filtrar por ${g}`}
              >
                {g}
                <span className="chip__num">{qtd}</span>
              </button>
            ))}
            {grupoFiltro && (
              <button className="chip chip--clear" type="button" onClick={() => setGrupoFiltro(null)} title="Limpar filtro">
                Limpar filtro x
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="grid-table grid-table--head">
          {renderColGroup()}

          <thead>
            <tr>
              {ORDER.map((key) => (
                <th key={key}>{LABELS[key]}</th>
              ))}
              <th>CONTROLES</th>
            </tr>
          </thead>
        </table>

        <div className="table-body-scroll">
          <table className="grid-table grid-table--body">
            {renderColGroup()}
            <tbody>
              {loading ? (
                <tr><td className="td" colSpan={ORDER.length + 1}>Carregando...</td></tr>
              ) : err ? (
                <tr><td className="td" colSpan={ORDER.length + 1}>{err}</td></tr>
              ) : filtradas.length === 0 ? (
                <tr><td className="td" colSpan={ORDER.length + 1}>Nenhum ativo encontrado.</td></tr>
              ) : (
                filtradas.map((row) => {
                  const isOn = !!Number(row.ativo);
                  return (
                    <tr key={row.id ?? JSON.stringify(row)}>
                      {ORDER.map((key) => (
                        <td key={key} className="td">{typeof row[key] === "string" ? UP(row[key]) : SAFE(row[key])}</td>
                      ))}
                      <td className="td td-controls">
                        <div className="actions actions--cluster">
                          <ToggleSwitch
                            on={isOn}
                            onToggle={() => {
                              setSelecionada(row);
                              if (isOn) setOpenInativar(true);
                              else setOpenAtivar(true);
                            }}
                            title={isOn ? "Inativar" : "Ativar"}
                          />
                          <span className="actions-divider" aria-hidden="true" />
                          <button
                            type="button"
                            className="icon-btn"
                            title="Editar"
                            onClick={() => {
                              setSelecionada(row);
                              setOpenEditar(true);
                            }}
                          >
                            <IconEdit />
                          </button>
                          <span className="actions-divider" aria-hidden="true" />
                          <button
                            type="button"
                            className="icon-btn"
                            title="Excluir"
                            onClick={() => {
                              setSelecionada(row);
                              setOpenExcluir(true);
                            }}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CriarModal open={openCriar} onClose={() => setOpenCriar(false)} onSalvar={criarAtivo} />
      <EditarModal
        open={openEditar}
        registro={selecionada}
        onClose={() => {
          setOpenEditar(false);
          setSelecionada(null);
        }}
        onSalvar={editarAtivo}
      />
      <ExcluirModal
        open={openExcluir}
        titulo={selecionada?.ativos}
        onClose={() => {
          setOpenExcluir(false);
          setSelecionada(null);
        }}
        onConfirmar={excluirAtivo}
      />
      <AtivarModal
        open={openAtivar}
        titulo={selecionada?.ativos}
        onClose={() => {
          setOpenAtivar(false);
          setSelecionada(null);
        }}
        onConfirmar={() => setAtivo(1)}
      />
      <InativarModal
        open={openInativar}
        titulo={selecionada?.ativos}
        onClose={() => {
          setOpenInativar(false);
          setSelecionada(null);
        }}
        onConfirmar={() => setAtivo(0)}
      />
    </div>
  );
}
